import { faBell } from '@fortawesome/pro-light-svg-icons';
import { Option } from '@spike/model';

import { FunctionComponent, useState } from 'react';
import EditSetting from './EditSetting';
import { useDispatch, useSelector } from 'react-redux';
import { useSettingsApiClientWrapper } from 'hooks';
import { CustomContentNotification } from '@spike/customcontentnotification-model';
import { RootState } from 'store';
import {
    CustomContentNotificationState,
    CustomContentNotificationStatus,
    updateOrCreateCustomContentNotification
} from '@spike/custom-content-notification-action';
import useNonInitialEffect from '@versiondos/hooks';
import { OverFullWindow, Spinner } from 'components/UI';
import EditNotificationContent from './EditCustomContentNotification/EditNotificationContent';

export interface Props {
    onEdit?: (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>, optionId: string) => void;
    loading: boolean;
}

export const EditAppointments: FunctionComponent<Props> = props => {
    const key = 'client_appointment_reminder';
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const [editContentKey, setEditContentKey] = useState<string>('');
    const [customContentNotification, setCustomContentNotification] = useState<CustomContentNotification>({
        key: '',
        content: '',
        name: '',
        marketplaceID: 0
    });
    const [loading, setLoading] = useState(false);

    const customContentNotificationState = useSelector<RootState, CustomContentNotificationState>(
        state => state.customContentNotifications
    );

    const noticeOptions = [
        { id: '2', name: '2 hrs before' },
        { id: '4', name: '4 hrs before' },
        { id: '8', name: '8 hrs before' },
        { id: '12', name: '12 hrs before' },
        { id: '24', name: '1 day before' },
        { id: '48', name: '2 days before' },
        { id: '72', name: '3 days before' }
    ];

    const handleEdit = (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>) => {
        props.onEdit && props.onEdit(emailChecked, smsChecked, noticeSelected, key);
    };

    const handleSave = () => {
        setLoading(true);
        setEditContentKey('');
        dispatch(updateOrCreateCustomContentNotification(apiClientWrapper, customContentNotification));
    };

    useNonInitialEffect(() => {
        if (
            customContentNotificationState.status === CustomContentNotificationStatus.UpdateSuccess &&
            customContentNotificationState.customContentNotification
        ) {
            setCustomContentNotification(customContentNotificationState.customContentNotification);
            setLoading(false);
        }
    }, [customContentNotificationState.status]);

    const subtitle = <>Reminds clients of their upcoming appointment.</>;

    return editContentKey !== '' ? (
        <OverFullWindow
            hideHeader={false}
            showFooter={true}
            title={editContentKey.includes('email') ? 'Edit Email' : 'Preview SMS'}
            centered={true}
            onClose={() => setEditContentKey('')}
            onBack={() => setEditContentKey('')}
            onSave={() => handleSave()}
        >
            <EditNotificationContent
                notificationKey={editContentKey}
                title={'Appointment Reminder'}
                subtitle={'Reminds clients of their upcoming appointment.'}
                headerIcon={faBell}
                sectionSubtitle={
                    'Keep reminders friendly and clear to reduce no-shows and keep pet parents on track for their appointments.'
                }
                customContentNotification={customContentNotification}
                setCustomContentNotification={setCustomContentNotification}
            />
        </OverFullWindow>
    ) : loading ? (
        <Spinner />
    ) : (
        <EditSetting
            optionId={key}
            noticeOptions={noticeOptions}
            headerIcon={faBell}
            title={'Appointment Reminder'}
            subtitle={subtitle}
            loading={props.loading}
            defaultOption={noticeOptions[4]}
            onEdit={handleEdit}
            showEditContent={true}
            onEditContent={setEditContentKey}
        />
    );
};

export default EditAppointments;
